/**
 * Route table and the gate in front of it.
 *
 * The gate is the interesting part. Six app states decide what renders, and
 * only one of them reaches the file browser:
 *
 *   signedOut    → bounce to mauvely.com, which owns accounts
 *   secondFactor → signed in, TOTP outstanding. Goes to the challenge, never to
 *                  /signin — signing in again only mints another such session
 *   unreachable  → we could not tell. Offer a retry rather than sending anyone
 *                  to sign in, because a transient fault is not a sign-out
 *   noVault      → first run, generate keys and a recovery phrase
 *   locked       → unlock in-browser; the server cannot do this for you
 *   unlocked     → the app
 *
 * `/s/…` sits outside the gate entirely: a share link has to work for someone
 * with no Mauvely account at all, and everything it needs is in the fragment.
 * `/picker` sits outside it too, and runs its own: it is a popup, and the
 * gate's bounce to mauvely.com would cut it off from the window that opened it.
 */

import React, { useEffect } from 'react';
import { matchRoute, useRouter } from './router';
import { useApp } from './state';
import { Shell } from './components/Shell';
import { Files } from './pages/Files';
import { SharedByMe, SharedWithMe } from './pages/Shared';
import { Workspaces } from './pages/Workspaces';
import { Workspace } from './pages/Workspace';
import { Project } from './pages/Project';
import { Computers } from './pages/Computers';
import { Trash } from './pages/Trash';
import { Audit } from './pages/Audit';
import { Courses } from './pages/Courses';
import { Admin } from './pages/Admin';
import { Settings } from './pages/Settings';
import { PublicShare } from './pages/PublicShare';
import {
  SecondFactorRequired,
  SignedOut,
  Unreachable,
  VaultRecover,
  VaultSetup,
  VaultUnlock
} from './pages/Vault';
import { AuthoriseApp } from './pages/AuthoriseApp';
import { Picker } from './pages/Picker';
import { isPickerPath } from './picker';
import { monoPlain } from './theme';
import { SkeletonBar, SkeletonLines } from './components/Skeleton';
import { useT } from './i18n';

export function App() {
  const t = useT();
  const { path } = useRouter();
  const { status } = useApp();

  // Before the gate: a public link must open for a stranger.
  if (path.startsWith('/s/')) return <PublicShare />;
  if (isPickerPath(path)) return <Picker />;

  if (status === 'loading') {
    return (
      // The shell's own skeleton: a sidebar block and a page of rows, so the
      // first paint is already the shape of the app rather than a centred word.
      // It has to be the *chrome* the shell will draw, not an approximation of
      // it — two panels on the tinted ground — or the app visibly restyles
      // itself the moment the session lands.
      <div
        className="mv-app"
        style={{
          minHeight: '100vh',
          background: 'var(--app-ground)',
          display: 'flex',
          gap: 16,
          padding: 16
        }}
      >
        <div
          style={{
            width: 236,
            flex: '0 0 auto',
            padding: '22px 18px',
            display: 'grid',
            gap: 14,
            alignContent: 'start',
            background: 'var(--app-chrome-panel)',
            borderRadius: 16
          }}
        >
          <SkeletonBar w="60%" h={20} />
          <div style={{ height: 12 }} />
          {[0, 1, 2, 3, 4].map((i) => (
            <SkeletonBar key={i} w="82%" h={13} />
          ))}
        </div>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            padding: '26px 30px',
            display: 'grid',
            gap: 20,
            alignContent: 'start',
            background: 'var(--app-panel)',
            borderRadius: 16
          }}
        >
          <SkeletonBar w={220} h={26} />
          <SkeletonLines lines={2} label={t('app.loading-mauvely-cloud')} />
        </div>
      </div>
    );
  }

  if (status === 'signedOut') return <SignedOut />;
  if (status === 'secondFactor') return <SecondFactorRequired />;
  if (status === 'unreachable') return <Unreachable />;
  if (status === 'noVault') return <VaultSetup />;
  if (status === 'locked') {
    return path === '/recover' ? <VaultRecover /> : <VaultUnlock />;
  }

  // Outside the Shell, and after the gate rather than before it. This is the
  // last step of signing an app in, not a place in the file browser — there is
  // no sidebar to be somewhere in. It has to be *after* the gate because it
  // needs the vault open to wrap anything, and the unlock screen it may pass
  // through does not navigate, so the URL fragment carrying the app's public key
  // is still there when it arrives.
  if (path.replace(/\/+$/, '') === '/authorise') return <AuthoriseApp />;

  return <Shell>{renderRoute(path)}</Shell>;
}

function renderRoute(path: string): React.ReactNode {
  const p = path.length > 1 ? path.replace(/\/+$/, '') : path;

  if (p === '/') return <Files />;
  if (p === '/shared') return <SharedWithMe />;
  if (p === '/shared-by-me') return <SharedByMe />;
  if (p === '/workspaces') return <Workspaces />;
  if (p === '/computers') return <Computers />;
  if (p === '/trash') return <Trash />;
  if (p === '/audit') return <Audit />;
  if (p === '/courses') return <Courses />;
  if (p === '/admin') return <Admin />;
  if (p === '/settings') return <Settings />;

  // Before the workspace pattern: `/workspaces/:id` would otherwise not match a
  // four-segment path at all, but keeping the more specific route first is the
  // habit that stops the next addition breaking.
  const project = matchRoute('/workspaces/:id/projects/:projectId', p);
  if (project?.id && project.projectId) {
    return <Project workspaceId={project.id} projectId={project.projectId} />;
  }

  const workspace = matchRoute('/workspaces/:id', p);
  if (workspace?.id) return <Workspace workspaceId={workspace.id} />;

  // `/o/<slug>` is retired. An enterprise account's cloud.mauvely.com already
  // *is* their company's, so the second address showed the same files under the
  // same brand and existed only to be kept in step.
  const company = matchRoute('/o/:slug', p);
  if (company?.slug) return <RedirectHome />;

  const folder = matchRoute('/files/:id', p);
  if (folder) return <Files folderId={folder.id} />;

  // An unlocked user who lands on /recover has nothing to recover.
  if (p === '/recover') return <Settings />;

  return <NotFound />;
}

/**
 * What is left of `/o/<slug>`.
 *
 * A redirect rather than a 404, because the path is in bookmarks and in mail
 * sent by customers, and it lands somewhere that is now the same place.
 * `replace`, so Back leaves the app rather than bouncing through here again.
 */
function RedirectHome() {
  const { navigate } = useRouter();
  useEffect(() => {
    navigate('/', { replace: true });
  }, [navigate]);
  return null;
}

function NotFound() {
  const t = useT();
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <h1
        style={{
          fontFamily: "var(--font-display), 'Baloo 2', system-ui",
          fontWeight: 800,
          fontSize: 34,
          color: 'var(--text-strong)',
          margin: 0
        }}
      >
        {t('app.page-not-found')}
      </h1>
      <span style={monoPlain}>{t('app.nothing-lives-at-this-address')}</span>
    </div>
  );
}
